/**
 * Equipment inspection level — the one place the level presets are written down.
 *
 * NOT employee certifications; that vocabulary lives in `certTypes.ts`. This is
 * the working-day inspection interval on an equipment record: `certDayLimit`
 * against `workingDaysSinceInspection`.
 *
 * There is no `cert_level` column. The chosen level is persisted as the free
 * text in `certDescription`, which the preset buttons stamp on click. That is
 * why this file exists: the buttons that WRITE the string and the screens that
 * READ it have to agree on the exact literal, and they were four separate
 * copies of it.
 *
 * The bug this closes: the Custom button set `certLevelOverride` (component
 * state, discarded on modal close) and never touched `certDescription`. A unit
 * moved from Level 4 to Custom @ 100 days saved `certDayLimit: 100` next to a
 * stale `certDescription: "CAODC Level 4"`, and every read-only screen printed
 * the stale string verbatim. Custom now stamps its own label, and
 * `certLevelLabel` repairs the rows written before it did.
 */

/** The label stamped when the operator picks their own day limit. */
export const CUSTOM_CERT_LEVEL = 'Custom';

export interface CertLevelPreset {
  /** Button label AND the exact string persisted to `certDescription`. */
  label: string;
  /** Working-day limit the preset applies. `null` = Custom: the operator types it. */
  days: number | null;
}

/**
 * Display order of the level buttons. Custom is last and carries no day limit —
 * picking it keeps whatever limit is already in the form and lets the operator
 * edit the Working Day Limit field directly.
 */
export const CERT_LEVEL_PRESETS: CertLevelPreset[] = [
  { label: 'CAODC Level 3', days: 250 },
  { label: 'CAODC Level 4', days: 500 },
  { label: CUSTOM_CERT_LEVEL, days: null },
];

/** True when `days` is a limit one of the CAODC presets owns (250 or 500). */
export function isPresetDayLimit(days: number | null | undefined): boolean {
  return CERT_LEVEL_PRESETS.some((p) => p.days != null && p.days === days);
}

/**
 * The level to display for a stored record.
 *
 * Repairs legacy rows only: a stored string is rewritten to "Custom" when it is
 * exactly a preset label AND the stored day limit contradicts that preset — the
 * one shape the old Custom branch could produce. Anything else is returned
 * verbatim, so operator free text ("Annual Working Day Certification") and a
 * genuine Level 4 @ 500 are both left alone.
 *
 * A record with no day limit is returned verbatim too: with nothing to
 * contradict the stored string, guessing "Custom" would be inventing a fact.
 *
 * Returns null when there is nothing to show, so call sites render `—` or skip
 * the row rather than printing an empty element.
 */
export function certLevelLabel(
  description: string | null | undefined,
  dayLimit: number | null | undefined,
): string | null {
  if (!description) return null;
  if (!Number.isFinite(dayLimit)) return description;

  const preset = CERT_LEVEL_PRESETS.find(
    (p) => p.days != null && p.label === description,
  );
  if (preset && dayLimit !== preset.days) return CUSTOM_CERT_LEVEL;

  return description;
}
